import React, { useMemo, useState } from 'react';
import { HiOutlineArrowNarrowRight, HiOutlineX } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { EventService } from 'src/event/service/event.service';
import { RegistrationService } from 'src/registration/service/registration.service';
import { RegistrationActions } from 'src/registration/ui/components/registration-actions';
import {
  LinkButton,
  MaterialSelect,
  StatusChip,
  Tooltip,
} from 'src/shared/index';
import {
  formatTime,
  parseTimeToMinutesInCompressedFormat,
} from 'src/shared/utils/timeFormat';
import './calendar.scss';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import {
  bindHover,
  bindPopover,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { nanoid } from 'nanoid';

export const YourProgramsCalendar = ({
  $registrations,
  $refetch,
  $upcomingEventsMapByRegistrationId = {},
  $inactiveDates = [],
}) => {
  console.log('🚀 ~ $inactiveDates:', $inactiveDates);
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);

  const shownRegistrations = React.useMemo(
    () =>
      RegistrationService.getRegistrationsByStatus($registrations, [
        'ACTIVE',
        'PAUSED',
        'WAITLISTED',
      ]),
    [$registrations],
  );

  const categories = React.useMemo(
    () => RegistrationService.getCategoryOptions(shownRegistrations),
    [shownRegistrations],
  );

  const registrations = useMemo(() => {
    return RegistrationService.filterRegistrations(
      shownRegistrations,
      search,
      selectedCategories,
    );
  }, [shownRegistrations, search, selectedCategories]);

  const daysOfWeek = [
    { day: 'Monday', value: 1 },
    { day: 'Tuesday', value: 2 },
    { day: 'Wednesday', value: 3 },
    { day: 'Thursday', value: 4 },
    { day: 'Friday', value: 5 },
    { day: 'Saturday', value: 6 },
    { day: 'Sunday', value: 0 },
  ];

  const parseTimeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(' ')[0].split(':').map(Number);
    const period = time.split(' ')[1];
    let totalMinutes = hours * 60 + minutes;
    if (period === 'PM' && hours !== 12) {
      totalMinutes += 12 * 60;
    }
    return totalMinutes;
  };

  const filterDataForDayOfWeek = (dayOfWeek) => {
    return registrations
      .filter((reg) => reg.event?.weekdays?.includes(dayOfWeek))
      .sort(
        (a, b) =>
          parseTimeToMinutes(formatTime(a.event?.startTime)) -
          parseTimeToMinutes(formatTime(b.event?.startTime)),
      );
  };

  const weekData = {
    Monday: filterDataForDayOfWeek(1),
    Tuesday: filterDataForDayOfWeek(2),
    Wednesday: filterDataForDayOfWeek(3),
    Thursday: filterDataForDayOfWeek(4),
    Friday: filterDataForDayOfWeek(5),
    Saturday: filterDataForDayOfWeek(6),
    Sunday: filterDataForDayOfWeek(0),
  };

  const getInactiveDatesForEvent = (eventId, day) => {
    const eventInactiveDates = $inactiveDates.find(
      (item) => +item.eventId === +eventId,
    );

    // Filter dates that match the current day of the week
    return (eventInactiveDates?.eventDates || []).filter((date) => {
      const dateObj = new Date(date.startDateTimeInUTC);
      const dayOfWeek = dateObj.getDay();
      return dayOfWeek === day.value;
    });
  };

  const UpcomingEventItem = ({ event, participantName }) => {
    const dateLimits = EventService.dateLimits(event);

    return (
      <div className="tw-p-2 tw-border-b tw-border-[#30cd92] tw-border-t">
        <div className="tw-font-semibold tw-text-xs">
          <Tooltip content={<p>{event.program?.name}</p>} direction="bottom">
            <p className="tw-line-clamp-1">{event.program?.name}</p>
          </Tooltip>
        </div>
        <div className="tw-text-xs">
          {event.startTime && event.endTime
            ? `${parseTimeToMinutesInCompressedFormat(event.startTime)} - ${parseTimeToMinutesInCompressedFormat(event.endTime)}`
            : ''}
        </div>
        <div className="fs-10 tw-py-0.5">{dateLimits}</div>
        <div className="tw-text-xs tw-text-[#01c786] tw-font-semibold tw-mt-1">
          Sign {participantName} up for the next season!
        </div>
        <div className="tw-flex tw-mt-1">
          <LinkButton
            className="!tw-bg-[#03c786] !tw-border-[#30cd92] tw-max-w-24 tw-text-xs tw-max-h-7"
            to={`/events/${event.code}`}
            target="_blank"
          >
            <div className="tw-flex tw-items-center tw-gap-1 tw-w-full tw-h-full tw-justify-center">
              <span>Enroll</span>
              <HiOutlineArrowNarrowRight size={12} />
            </div>
          </LinkButton>
        </div>
      </div>
    );
  };

  const InactiveDatesPopup = ({ inactiveDates = [], itemKey }) => {
    const popupState = usePopupState({
      popupId: `inactive-dates-${itemKey}`,
    });

    if (!inactiveDates?.length) return null;

    return (
      <div>
        <div
          className="!tw-text-xs !tw-text-[#C81E1E] !tw-bg-[#f7e6e6] tw-cursor-pointer tw-p-1 tw-border-b tw-border-[#ccc] tw-border-solid tw-text-center"
          {...bindHover(popupState)}
        >
          <div className="tw-w-fit tw-underline">No Class Dates</div>
        </div>
        <HoverPopover
          {...bindPopover(popupState)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <div className="tw-p-4 tw-max-w-xs tw-bg-white tw-shadow-lg tw-rounded-md">
            <div className="tw-flex tw-justify-between tw-items-center tw-font-semibold tw-mb-3 tw-gap-4 tw-text-[#C81E1E] tw-border-b tw-border-[#f7e6e6] tw-pb-2">
              <span>No Class Dates</span>
              <button
                onClick={popupState.close}
                className="tw-p-1 hover:tw-bg-gray-100 tw-rounded-full"
              >
                <HiOutlineX className="tw-w-4 tw-h-4 tw-text-[#C81E1E]" />
              </button>
            </div>
            <ul className="tw-space-y-2">
              {inactiveDates.map((date) => (
                <li
                  key={date.id}
                  className="tw-text-sm tw-flex tw-items-center"
                >
                  <span className="tw-w-2 tw-h-2 tw-bg-[#C81E1E] tw-rounded-full tw-mr-2"></span>
                  {new Date(date.startDateTimeInUTC).toLocaleDateString(
                    'en-US',
                    {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    },
                  )}
                </li>
              ))}
            </ul>
          </div>
        </HoverPopover>
      </div>
    );
  };

  return (
    <div className="main-calendar-container">
      <div className="schedule-title flex justify-space-between">
        <div className="selectContainer">
          <MaterialSelect
            options={categories}
            label="Filter by category"
            multiple
            onChange={(value) => setSelectedCategories(value)}
            value={selectedCategories}
            isClearable
          />
        </div>
        <input
          type="search"
          placeholder="Search"
          className="search"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div className="grid-container">
        {daysOfWeek.map((day, index) => (
          <div
            key={`${day.day}-${index}`}
            className={`${
              index > 5 ? 'bottom-left-border' : 'bottom-right-border'
            }`}
          >
            <div className="grid-header">
              <div className="grid-item">{day.day}</div>
            </div>
            {weekData[day.day].map((reg) => {
              const dateLimits = EventService.dateLimits(reg?.event);
              const upcomingEvents =
                $upcomingEventsMapByRegistrationId?.[reg.id];
              const inactiveDates = getInactiveDatesForEvent(
                reg.event?.id,
                day,
              );
              const itemKey = `${reg.id}-${reg.event?.id}-${day.day}`;
              return (
                <>
                  <div key={itemKey} className="cell">
                    <div>
                      <div className="grid-item tw-font-semibold flex align-center justify-space-between">
                        <Tooltip
                          content={<p>{reg.event?.program?.name}</p>}
                          direction="bottom"
                        >
                          <p className="tw-line-clamp-1">
                            {reg.event?.program?.name}
                          </p>
                        </Tooltip>
                        <RegistrationActions
                          showButton={false}
                          $registration={reg}
                          $refetch={$refetch}
                        />
                      </div>
                      <div className="grid-item tw-font-semibold flex align-center justify-space-between">
                        {reg.event?.startTime && reg.event?.endTime ? (
                          `${parseTimeToMinutesInCompressedFormat(
                            reg.event?.startTime,
                          )} - ${parseTimeToMinutesInCompressedFormat(
                            reg.event?.endTime,
                          )}`
                        ) : (
                          <></>
                        )}
                      </div>
                      <div className="grid-item fc-primary-45">
                        <Tooltip
                          content={
                            <p>
                              {reg.registrationEventName ||
                                reg.event.effectiveName ||
                                reg.event.label}
                            </p>
                          }
                          direction="bottom"
                        >
                          <p className="tw-line-clamp-3">
                            <Link
                              to={`/events/spotlight/${reg.event.code}`}
                              target="_blank"
                              className="tw-max-w-[120px] tw-inline-block tw-break-words"
                            >
                              {reg.registrationEventName ||
                                reg.event.effectiveName ||
                                reg.event.label}
                            </Link>
                          </p>
                        </Tooltip>
                      </div>
                      <div className="tw-px-1.5 fs-10 tw-pb-0.5">
                        {dateLimits}
                      </div>
                      <div style={{ paddingTop: 0 }} className="grid-item">
                        <Tooltip
                          content={
                            <p>
                              {reg.participant?.firstName}{' '}
                              {reg.participant?.lastName}
                            </p>
                          }
                          direction="bottom"
                        >
                          <p className="tw-w-24 sm:tw-w-18 md:tw-w-26 lg:tw-w-28 tw-line-clamp-2">
                            {reg.participant?.firstName}{' '}
                            {reg.participant?.lastName}{' '}
                          </p>
                        </Tooltip>
                        <p className="fs-12">
                          (
                          <Link to={`/registrations/${reg.id}`} target="_blank">
                            {reg.id}
                          </Link>
                          )
                        </p>
                        <p className="pt-8">
                          {reg.status === 'WAITLISTED' && (
                            <div className="p-8">
                              <StatusChip $level="warning">Waitlist</StatusChip>
                            </div>
                          )}
                        </p>
                        {reg.cancelRegistrationRequest && (
                          <div className="fs-11 fc-gray-30 mt-10">
                            {RegistrationService.getShortCancelRequestValue(
                              reg.cancelRegistrationRequest,
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <InactiveDatesPopup
                    inactiveDates={inactiveDates}
                    itemKey={itemKey}
                  />
                  {upcomingEvents?.length > 0 && (
                    <div className="tw-bg-[#e6f7e6] tw-border-[#30cd92]">
                      {weekData[day.day].map((reg) =>
                        $upcomingEventsMapByRegistrationId[reg.id]?.map(
                          (event) => (
                            <UpcomingEventItem
                              key={event.id}
                              event={event}
                              registrationId={reg.id}
                              participantName={
                                reg.participant?.firstName || 'participant'
                              }
                            />
                          ),
                        ),
                      )}
                    </div>
                  )}
                </>
              );
            })}
          </div>
        ))}
      </div>
      {shownRegistrations.length === 0 && (
        <p className="text-center fw-500 pa-14">
          You have no active registrations yet
        </p>
      )}
    </div>
  );
};
